import { DocIcon, LinkIcon, SoundIcon } from '@/components/investors/icons/Icons';
import { MediumSmallTitle, SmallUppercase } from '@/components/investors/shared';
import { SubcritionForm } from '@/components/investors/Subscription';
import { SeoProps } from '@/components/shared/SEO';
import { InvestorsPageTemplate } from '@/pageTemplate/InvestorsPageTemplage';
import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { Option, OptionsList, Select } from '@lemonade-hq/nuis';
import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { fetchWithRetry } from '../../utils/fetchWithRetry';

type IconType = 'audio' | 'document' | 'link';

type Event = {
  id: string;
  date: string;
  title: string;
  links: { iconType: IconType; url: string; label: string }[];
  year?: number;
};
type Events = {
  upcoming: Event[];
  past: Event[];
};

const EventsIntroItemWrapper = styled(Flex)<{ isPast?: boolean }>`
  padding-block: 16px;
  border-bottom: 1px solid ${themedColor('glass')};

  &:last-child {
    border-bottom: ${({ isPast }) => (isPast ? 'none' : ` 1px solid ${themedColor('glass')}`)};
  }
`;

const FileLink = styled(Link)`
  font-size: 1.4rem;
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 20px;
`;

const FilterTitle = styled.label`
  font-size: 1.6rem;
  text-align: left;
  color: ${themedColor('textPrimary')};
  font-weight: 400;
  white-space: nowrap;
`;

const Content = styled.p`
  ${font('main', { fontSize: '1.6rem', fontWeight: 400, lineHeight: '145%' })};
  text-align: left;
  text-wrap: pretty;
`;

export const ICONS = {
  audio: SoundIcon,
  document: DocIcon,
  link: LinkIcon,
} as Record<IconType, React.ComponentType>;

const EventsIntroItem: React.FC<Omit<Event, 'year' | 'id'> & { isPast?: boolean }> = ({
  date,
  title,
  links,
  isPast,
}) => {
  return (
    <EventsIntroItemWrapper flexDirection="column" gap="4px" isPast={isPast}>
      <SmallUppercase>{date}</SmallUppercase>
      <Content>{title}</Content>
      <Flex flexDirection={'row'} flexWrap={'wrap'} mt="12px">
        {links.map(({ iconType, url, label }) => {
          const Icon = ICONS[iconType];
          return (
            <FileLink key={url} href={url} target="_blank" rel="noopener noreferrer">
              <Icon />
              {label}
            </FileLink>
          );
        })}
      </Flex>
    </EventsIntroItemWrapper>
  );
};

const filteredEventsBySelectedYear = (events: Event[], selectedYear: number) => {
  return events.filter(({ year }) => year === selectedYear);
};

const FilteredEvents: React.FC<{ events: Event[] }> = ({ events }) => {
  const years = useMemo(() => Array.from(new Set(events.map(event => event.year))), [events]);

  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  useEffect(() => {
    if ((years ?? []).length === 0) return;
    setSelectedYear(years[0] as number);
  }, [years]);

  if (selectedYear == null) return null;

  return (
    <>
      <Flex alignItems="center" gap="8px" width="100%">
        <FilterTitle>Select year</FilterTitle>
        <Box width={['100%', '160px']} position="relative">
          <Select
            value={selectedYear as unknown as string}
            onChange={value => setSelectedYear(Number(value))}
            size="medium"
          >
            <OptionsList>
              {years.map(year => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </OptionsList>
          </Select>
        </Box>
      </Flex>
      <Flex flexDirection="column" mt="10px" width="100%">
        {filteredEventsBySelectedYear(events, selectedYear).map(({ id, date, title, links }) => {
          return <EventsIntroItem key={id} date={date} title={title} links={links} isPast />;
        })}
      </Flex>
    </>
  );
};

const MAX_WIDTH = 700;

const seo = {
  title: 'Lemonade Inc. - Events',
  description:
    'Find details on upcoming and past Lemonade investor events, including earnings calls, conferences, webcasts, and shareholder meetings.',
} as SeoProps;

export default function EventsPage({ events }: { events: Events }) {
  return (
    <InvestorsPageTemplate seo={seo}>
      <Flex flexDirection="column" gap="20px" width="100%" maxWidth={MAX_WIDTH} px={[20, 20, 0]}>
        <Flex flexDirection="column" gap="6px">
          <MediumSmallTitle>Upcoming Events</MediumSmallTitle>
          {events.upcoming.length === 0 && (
            <Flex flexDirection="column" gap="16px">
              <Content>
                There are no events scheduled at the moment.
                <br />
                Leave your email to get notified when a new event will be announced.
              </Content>
              <SubcritionForm />
            </Flex>
          )}

          <Flex flexDirection="column" mt="10px" width="100%">
            {events.upcoming.map(({ id, date, title, links }) => {
              return <EventsIntroItem key={id} date={date} title={title} links={links} />;
            })}
          </Flex>
        </Flex>
        <Flex flexDirection="column" gap="18px">
          <MediumSmallTitle>Past Events</MediumSmallTitle>
          {events.past.length === 0 && (
            <Flex flexDirection="column" gap="16px">
              <Content>There are no past events at the moment.</Content>
            </Flex>
          )}
          <FilteredEvents events={events.past} />
        </Flex>
      </Flex>
    </InvestorsPageTemplate>
  );
}

const getEvents = async (): Promise<{ data: Events }> => {
  try {
    return await fetchWithRetry<{ data: Events }>(`${process.env.NEXT_PUBLIC_INVESTORS_URL}/events`);
  } catch (err) {
    console.error('feeds error', err);
    return {
      data: {
        upcoming: [],
        past: [],
      },
    };
  }
};

// Super short example using new keybinding !!!!
export const getStaticProps = async () => {
  const { data: events } = await getEvents();
  return {
    props: {
      events,
    },
    revalidate: 10, // 10 seconds, we need this to be wired as much as possible to the data source
  };
};
